import {
  Button,
  Grid,
  Typography,
  CircularProgress,
  Hidden,
  Checkbox
} from '@material-ui/core'
import i18n from 'i18next'
import { path } from 'ramda'
import { FC, Fragment, useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { UpdateStatus } from '../../../../actions/types'
import { ChipList } from '../../../../components/Display/Lists'
import { Message } from '../../../../components/Messages'
import {
  isApartmentCancelledDisabled,
  isApartmentMarketingDisabled
} from '../../../../libs/statuses'
import { MuiClasses } from '../../../../types'
import {
  Apartment,
  ApartmentStatus,
  CheckboxPropertyKeys
} from '../../../../types/apartments'
import { IndexedApplications } from '../../../../types/states'
import { User } from '../../../../types/users'
import { getSortedKeyArray, returnTrueFirst } from '../../../../utils/sorting'
import { getStepPath } from '../../../../utils/steps'
import {
  getAdditionalPropertiesForOverview,
  getApartmentBuildingInfo
} from '../../Forms/values/apartmentInitialValues'
import { ApartmentExpansionPanel, ApartmentInformationList } from '../Lists'
import { navigate } from '../../../../actions/routes'
import { useDispatch } from 'react-redux';
import '../../../PayForPublish/Rentapp.css';
import apartmentAPI from '../../../../services/apartments'
import { toast } from 'react-toastify'
import Dialog from '../../../Dialog'
import PaymentPopup from '../../../PayForPublish/PaymentPopup'
import Suggest from '../../Stepper/Steps/Suggest'
import DigivakuusFaqLink from '../../../../components/Digivakuus/DigivakuusFaqLink'

export interface ApartmentInfoProps {
  t: i18n.TFunction
  classes: MuiClasses
  apartment: Apartment
  user: User | undefined
  isAdmin: boolean
  updateStatus: UpdateStatus
  applications: IndexedApplications
}

const ApartmentInfo: FC<ApartmentInfoProps> = ({
  t,
  classes,
  apartment,
  apartment: {
    applicationIds,
    status,
    information,
    confirmation,
    apartmentId,
    code
  },
  user,
  isAdmin,
  updateStatus,
  applications,
}) => {
  const [selectedApplicationIdForRented, setselectedApplicationIdForRented] = useState("");
  const [isLoading, setIsLoading] = useState({ loading: false, func: '' })
  const wantsPriceEstimate = confirmation.requirePriceEstimate
  const promocode = confirmation.promocode
  const selectedApplication =
    applicationIds &&
      applicationIds?.contractWantedId &&
      applications[apartmentId]
      ? applications[apartmentId].find(
        (application) =>
          application.applicationId === applicationIds.contractWantedId
      )
      : undefined
  const selectedContractWantedName = selectedApplication?.data?.name
    ? selectedApplication?.data?.name
    : undefined
  useEffect(() => {
    setIsLoading({
      loading: false,
      func: ''
    })
  }, [status])

  const dispatch = useDispatch();

  const redirectToPaymentPage = () => {
    dispatch(navigate(`/users/${user.userId}/apartments/${apartment.apartmentId}/payment`));
  };

  const handleApplicationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setselectedApplicationIdForRented(event.target.value);
  };

  const [updateStatusToRentedClicked, setUpdateStatusToRentedClicked] = useState(false)
  const [updateStatusToRentedDialogHidden, setUpdateStatusToRentedDialogHidden] = useState(false)
  const [digivakuusDepositAccepted, setDigivakuusDepositAccepted] = useState(false)
  const [rentInsuranceInquiryAccepted, setRentInsuranceInquiryAccepted] = useState(false)

  const hideUpdateStatusToRentedDialog = () => {
    setDigivakuusDepositAccepted(false)
    setRentInsuranceInquiryAccepted(false)
    setUpdateStatusToRentedDialogHidden(true)
  }

  const unHideUpdateStatusToRentedDialog = () => {
    setUpdateStatusToRentedClicked(false)
    setDigivakuusDepositAccepted(false)
    setRentInsuranceInquiryAccepted(false)
    setUpdateStatusToRentedDialogHidden(false)
  }

  const proceedToSettingToRented = () => {
    if (selectedApplicationIdForRented === "") {
      return
    }

    setUpdateStatusToRentedClicked(true)
  }

  const collateralEligible = (): boolean => {
    // hard false conditions
    if (
      !selectedApplicationIdForRented ||
      selectedApplicationIdForRented === "" ||
      selectedApplicationIdForRented === "someoneElse" ||
      !user?.collateralVerified
    ) {
      return false
    }

    // flatten applications -> array
    const allApplications = Object.values(applications || {}).flat()

    // keep only this apartment's applications
    const forCurrentApartment = allApplications.filter(
      (a: any) => a.apartmentId === apartment.apartmentId
    )

    if (forCurrentApartment.length === 0) return false

    // select target application
    const targetApp =
      forCurrentApartment.length === 1
        ? forCurrentApartment[0]
        : forCurrentApartment.find(
          (a: any) => a.applicationId === selectedApplicationIdForRented
        ) || forCurrentApartment[0]

    if (!targetApp) return false

    if (targetApp?.metadata?.digivakuusDepositCreated === true) {
      return false
    }

    // check applicants
    const hasEligibleApplicant =
      Array.isArray(targetApp.applicants) &&
      targetApp.applicants.some(
        (appl: any) =>
          appl?.acceptedDigivakuusDepositService === true &&
          appl?.digivakuusEligibility === true
      )

    return hasEligibleApplicant
  }

  const updateStatusToRented = async (digivakuusDepositAccepted: boolean) => {
    const status = ApartmentStatus.rented
    setIsLoading({ loading: true, func: 'rent' })
    const data: Record<string, unknown> = {
      status,
      selectedApplicationIdForRented,
      digivakuusDepositAccepted
    }
    if (rentInsuranceInquiryAccepted) {
      data.acceptedService = 'insuranceInquiry'
    }
    setUpdateStatusToRentedClicked(false)
    hideUpdateStatusToRentedDialog()
    try {
      await apartmentAPI.UpdateStatusToRented(user.userId, apartmentId, data)
      apartment.status = ApartmentStatus.rented
      localStorage.setItem('statusUpdatedToRented', 'true')
      const message = t('apartment.overview.setRentedSuccess')
      toast.info(message, { autoClose: 25000 })
    } catch (error) {
      const message = t('apartment.overview.setRentedFailed')
      toast.info(message, { autoClose: 25000 })
    }
    setIsLoading({ loading: false, func: '' })
  }
  // useEffect(() => {
  //   const firstApplicationId = Object.values(applications)
  //     .flat()
  //     .filter(application =>
  //       application.status !== 'rejected' &&
  //       application.status !== 'notInterested' &&
  //       application.apartmentId === apartmentId
  //     )[0]?.applicationId;

  //   if (firstApplicationId) {
  //     setselectedApplicationIdForRented("");
  //   }
  // }, [])

  useEffect(() => {
    const statusUpdatedToRented = localStorage.getItem('statusUpdatedToRented');
    if (statusUpdatedToRented) {
      hideUpdateStatusToRentedDialog();
      localStorage.removeItem('statusUpdatedToRented');
    }
  }, []);

  return (
    <Fragment>
      {information && confirmation && (
        <Grid
          container
          direction='column'
          item
          sm={12}
          md={12}
          lg={5}
          className={classes.textContainer}
          justify='space-between'
        >
          <Grid
            container
            item
            xs={12}
            className={classes.informationRigthSideContainer}
            style={{ paddingTop: isAdmin ? '30px' : '20px' }}
          >
            <Grid
              item
              xs={12}
              sm={12}
              lg={12}
              className={classes.informationContainer}
            >
              {isAdmin && apartment && (
                <Fragment>
                  {(wantsPriceEstimate || promocode) && (
                    <Typography variant='h6' style={{ color: '#3f51b5' }}>
                      {promocode && `Etukoodi: ${confirmation.promocode}`}
                      {promocode && wantsPriceEstimate && ', '}
                      {wantsPriceEstimate && 'Haluaa hinta-arvion!'}
                    </Typography>
                  )}
                  <Typography
                    variant='h6'
                    style={{ color: '#3f51b5', marginBottom: '10px' }}
                  >
                    {`${t('apartment.overview.apartmentCode')}: ${code}`}
                  </Typography>
                </Fragment>
              )}
              <Grid
                item
                xs={12}
                style={{ paddingBottom: '20px', textAlign: 'left' }}
              ><Typography variant='h5'>{t('apartment.propertyInformation')}</Typography>
                <Grid item style={{ marginLeft: 'auto', marginTop: '12px' }}>
                  <Button
                    component={RouterLink}
                    to={getStepPath(apartment, true)}
                    size='small'
                    style={{ marginRight: '10px' }}
                    className={classes.startMarketing}
                  >
                    {t('apartmentOverview.editApartment')}
                  </Button>
                  {status === 'marketing' && isAdmin && (
                    <Dialog
                      dialogId='UpdateStatusToCancelled'
                      disabled={isApartmentCancelledDisabled(status)}
                      color='secondary'
                      paperMaxWidth='1070px'
                      title={t('apartments.cancel.label')}
                      buttonLabel={
                        isLoading.loading && isLoading.func === 'cancel' ? (
                          <CircularProgress
                            size={23}
                            style={{ color: '#ff0000' }}
                          />
                        ) : (
                          t('adminApartments.changeStatus.toCancelled')
                        )
                      }
                      buttonClassName={classes.cancelApartment}
                      confirmDialogSubmitAction={() => {
                        setIsLoading({
                          loading: true,
                          func: 'cancel'
                        })
                        updateStatus(ApartmentStatus.cancelled)
                      }}
                      Content={
                        <Message
                          message={t('apartments.cancel.confirmMessage', {
                            address: path(['address'], information) || ''
                          })}
                        />
                      }
                    />
                  )}
                  {status === 'confirmed' && isAdmin && (
                    <>
                      <Dialog
                        dialogId='UpdateStatusToCancelled'
                        disabled={isApartmentCancelledDisabled(status)}
                        color='secondary'
                        paperMaxWidth='1070px'
                        title={t('apartments.cancel.label')}
                        buttonLabel={
                          isLoading.loading && isLoading.func === 'cancel' ? (
                            <CircularProgress
                              size={23}
                              style={{ color: '#ff0000' }}
                            />
                          ) : (
                            t('adminApartments.changeStatus.toCancelled')
                          )
                        }
                        buttonClassName={classes.cancelApartment}
                        confirmDialogSubmitAction={() => {
                          setIsLoading({
                            loading: true,
                            func: 'cancel'
                          })
                          updateStatus(ApartmentStatus.cancelled)
                        }}
                        Content={
                          <Message
                            message={t('apartments.cancel.confirmMessage', {
                              address: path(['address'], information) || ''
                            })}
                          />
                        }
                      />
                    </>
                  )}
                  {status === 'rentContractWanted' && (
                    <>
                      <Button
                        size='small'
                        className={classes.cancelApartment}
                        disabled={isApartmentMarketingDisabled(status)}
                        onClick={() => {
                          setIsLoading({
                            loading: true,
                            func: 'market'
                          })
                          //updateStatus(ApartmentStatus.marketing)
                          redirectToPaymentPage()
                        }}
                      >
                        {isLoading.loading && isLoading.func === 'market' ? (
                          <CircularProgress
                            size={23}
                            style={{ color: '#ff0000' }}
                          />
                        ) : (
                          t('adminApartments.changeStatus.rentedToMarketing')
                        )}
                      </Button>
                    </>
                  )}

                  {(status === 'rentContractWanted' || status === 'marketing' || status === ApartmentStatus.followUp) && (
                    <>
                      {updateStatusToRentedDialogHidden ? (
                        <Button
                          className={classes.startMarketing}
                          style={{ textTransform: 'uppercase' }}
                          onClick={unHideUpdateStatusToRentedDialog}
                          disabled={isLoading.loading && isLoading.func === 'rent'}
                        >
                          {isLoading.loading && isLoading.func === 'rent' ? (
                            <CircularProgress size={23} style={{ color: 'white' }} />
                          ) : (
                            t('adminApartments.changeStatus.toRented')
                          )}
                        </Button>
                      ) : (
                        <Dialog
                          maxWidth='md'
                          onClose={() => {
                            setUpdateStatusToRentedClicked(false)
                            setselectedApplicationIdForRented("")
                            setDigivakuusDepositAccepted(false)
                            setRentInsuranceInquiryAccepted(false)
                          }}
                          hideDialog={updateStatusToRentedDialogHidden}
                          dialogId='UpdateStatusToRented'
                          buttonClassName={classes.startMarketing}
                          //title={updateStatusToRentedClicked ? t('apartments.rented.titleWhenClicked') : t('apartments.rented.label')}
                          title={t('apartments.rented.label')}
                          agreeButtonDisabled={selectedApplicationIdForRented === ""}
                          buttonLabel={
                            isLoading.loading && isLoading.func === 'rent' ? (
                              <CircularProgress size={23} style={{ color: 'white' }} />
                            ) : (
                              t('adminApartments.changeStatus.toRented')
                            )
                          }
                          fullScreenDialogMobile={true}
                          Content={
                          <>
                            {!updateStatusToRentedClicked ? (
                              <>
                                <Grid container spacing={2} style={{ marginTop: 16 }}>

                                  <Grid item sm={12} style={{ marginTop: '-16px' }}>
                                    <Message
                                      message={t('apartments.rented.confirmMessageGeneral', {
                                        applicantName: selectedContractWantedName || t('application.noApplicationFound'),
                                      })}
                                    />
                                  </Grid>

                                  <Grid item sm={12} style={{ marginTop: '-16px' }}>
                                    <select
                                      className="select-dropdown"
                                      onChange={handleApplicationChange}
                                      value={selectedApplicationIdForRented}
                                    >
                                      <option value="" disabled hidden>{t('apartments.rented.chooseTenant')}</option>
                                      {Object.values(applications)
                                        .flat()
                                        .filter(application => application.apartmentId === apartmentId && application.status !== 'rejected' && application.status !== 'notInterested')
                                        .map((application, index) => (
                                          <option key={index} value={application.applicationId}>
                                            {application.data.name}
                                          </option>
                                        ))}
                                      <option value="someoneElse">{t('apartments.rented.someoneElse')}</option>
                                    </select>
                                  </Grid>

                                  <Grid item sm={12} style={{ marginTop: '-16px' }}>
                                    <Suggest
                                      content={t('apartments.rented.chooseTenantHelper')}
                                      containerMaxWidth='100%'
                                      contentMaxWidth='100%'
                                      isModal
                                      padding='25px 0px 15px 0'
                                    />
                                  </Grid>

                                  <Grid item xs={6} style={{ paddingRight: 8 }}>
                                    <Button
                                      fullWidth
                                      //className={classes.dialogSaveBtn}
                                      //style={{ textTransform: 'uppercase' }}
                                      style={{
                                        boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.15)',
                                        padding: '8px'
                                      }}
                                      onClick={() => hideUpdateStatusToRentedDialog()}
                                    >
                                      {t('apartments.rented.cancel')}
                                    </Button>
                                  </Grid>
                                  <Grid item xs={6} style={{ paddingLeft: 8 }}>
                                    {!collateralEligible() ? (
                                      <Button
                                        variant="contained"
                                        fullWidth
                                        disabled={!selectedApplicationIdForRented || selectedApplicationIdForRented === ""}
                                        className={classes.primaryBtn}
                                        onClick={() => updateStatusToRented(false)}
                                      >
                                        {t('general.submit')}
                                      </Button>
                                    ) : (
                                      <Button
                                        variant="contained"
                                        fullWidth
                                        className={classes.primaryBtn}
                                        onClick={() => proceedToSettingToRented()}
                                      >
                                        {t('apartments.rented.accept')}
                                      </Button>
                                    )}
                                  </Grid>
                                </Grid>
                              </>
                            ) : (
                              <>
                                <Grid container justify="center" style={{ margin: 'auto' }}>
                                  <Typography variant="h5" align="center" className={classes.modalTitle} style={{ marginTop: '8px' }}>
                                    {t('apartments.rented.depositServiceTitle')}
                                  </Typography>

                                  <Grid item sm={12} style={{ marginTop: '-16px' }}>
                                    <Suggest isModal containerMaxWidth="100%" contentMaxWidth="100%" content={t('apartments.rented.depositServiceInfo')} />
                                  </Grid>

                                  <div className={classes.acceptBox}>
                                    <div className={classes.acceptRow}>
                                      <Checkbox
                                        classes={{ root: classes.checkboxRoot, checked: classes.checkboxChecked }}
                                        checked={digivakuusDepositAccepted}
                                        onChange={e => setDigivakuusDepositAccepted(e.target.checked)}
                                      />
                                      <div className={classes.acceptTexts}>
                                        <Typography className={classes.acceptHeader}>
                                          {t('apartments.rented.depositServiceCheckboxHeader')}
                                        </Typography>
                                        <Typography className={classes.acceptText}>
                                          {t('apartments.rented.depositServiceCheckboxContent')}
                                        </Typography>
                                        <div className={classes.digivakuusFaqInlineWrapper}>
                                          <DigivakuusFaqLink
                                            t={t}
                                            variant='landlord'
                                            buttonClassName={classes.digivakuusFaqButton}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <div className={classes.acceptBox}>
                                    <div className={classes.acceptRow}>
                                      <Checkbox
                                        classes={{ root: classes.checkboxRoot, checked: classes.checkboxChecked }}
                                        checked={rentInsuranceInquiryAccepted}
                                        onChange={e => setRentInsuranceInquiryAccepted(e.target.checked)}
                                      />
                                      <div className={classes.acceptTexts}>
                                        <Typography className={classes.acceptHeader}>
                                          {t('apartments.rented.rentChargingInquiryTitle')}
                                        </Typography>
                                        <Typography className={classes.acceptText}>
                                            {t('apartments.rented.rentChargingInquiryInfo')}
                                        </Typography>
                                        <div className={classes.digivakuusFaqInlineWrapper}>
                                          <DigivakuusFaqLink
                                            t={t}
                                            variant='rentInsurance'
                                            buttonClassName={classes.digivakuusFaqButton}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <Grid container spacing={2} style={{ marginTop: '6px' }}>
                                    <Grid item xs={12}>
                                      <Button
                                        variant="contained"
                                        fullWidth
                                        className={classes.primaryBtn}
                                        onClick={() => updateStatusToRented(digivakuusDepositAccepted)}
                                      >
                                        {t('apartments.rented.submit')}
                                      </Button>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </>
                            )}
                          </>
                        }
                      />
                      )}
                      {status !== ApartmentStatus.followUp ? (
                        <Dialog
                          dialogId='FollowUpStatusPaymentPopup'
                          color='secondary'
                          size='large'
                          maxWidth='md'
                          fullScreenDialogMobile={true}
                          buttonLabel={t('adminApartments.changeStatus.toFollowUp')}
                          buttonClassName={`${classes.startMarketing} ${classes.followUpButton}`}
                          Content={
                            <PaymentPopup
                              productTags={['setToFollowUpStatus']}
                              headertext={t('paymentPopup.headerTexts.setToFollowUpStatus')}
                              infotext={t('paymentPopup.suggestTexts.setToFollowUpStatus')}
                              apartment={apartment}
                            />
                          }
                        />
                      ) : (
                        <Button
                          className={`${classes.startMarketing} ${classes.followUpButton}`}
                          onClick={() => {
                            setIsLoading({
                              loading: true,
                              func: 'followUpToMarketing'
                            })
                            updateStatus(ApartmentStatus.marketing)
                          }}
                          disabled={
                            isLoading.loading && isLoading.func === 'followUpToMarketing'
                          }
                        >
                          {isLoading.loading && isLoading.func === 'followUpToMarketing' ? (
                            <CircularProgress size={23} style={{ color: 'white' }} />
                          ) : (
                            t('adminApartments.changeStatus.rentedToMarketing')
                          )}
                        </Button>
                      )}
                    </>
                  )}
                  {(status === 'cancelled' || status === 'rented' || status === 'confirmed') && (
                    <Button
                      size='small'
                      className={classes.startMarketing}
                      disabled={isApartmentMarketingDisabled(status)}
                      onClick={() => {
                        setIsLoading({
                          loading: true,
                          func: 'market'
                        })
                        redirectToPaymentPage()
                        //updateStatus(ApartmentStatus.marketing)
                      }}
                    >
                      {isLoading.loading && isLoading.func === 'market' ? (
                        <CircularProgress size={23} style={{ color: 'white' }} />
                      ) : (
                        t('adminApartments.changeStatus.toMarketing')
                      )}
                    </Button>
                  )}
                  <Hidden lgUp>
                    <Grid item xs={12} sm={12} lg={12} className={classes.mt20}>
                      <Grid item xs={12} className={classes.informationContainer}>
                        <Typography className={classes.infoTitle} variant='h5'>
                          {t('apartment.list.information.notes')}
                        </Typography>
                        <Typography variant='body1'>
                          <pre className='MuiTypography-root MuiTypography-body1'>
                            {apartment?.information?.notes || ''}
                          </pre>
                        </Typography>
                      </Grid>
                      {status === 'marketing' && (
                        <Grid item xs={12} className={classes.informationContainer}>
                          <Typography className={classes.infoTitle} variant='h5'>
                            {t('apartment.list.information.listingVisibility')}
                          </Typography>
                          <Suggest
                            content={t('apartment.listingVisibilityHelper')}
                            padding='0px 0px 25px 0px'
                            containerMaxWidth='100%'
                            contentMaxWidth='100%'
                          />
                          <Dialog
                            dialogId='PaymentPopup'
                            //disabled={isApartmentCancelledDisabled(status)}
                            color='secondary'
                            size='large'
                            maxWidth='md'
                            fullScreenDialogMobile={true}
                            //title={t('apartments.cancel.label')}
                            buttonLabel={
                              isLoading.loading && isLoading.func === 'cancel' ? (
                                <CircularProgress size={23} style={{ color: 'white' }} />
                              ) : (
                                t('apartment.refreshOnPlatforms')
                              )
                            }
                            buttonClassName={classes.startMarketing}
                            Content={
                              <PaymentPopup
                                productTags={['refreshOnPlatforms']}
                                headertext={t('paymentPopup.headerTexts.refreshOnPlatforms')}
                                infotext={t('paymentPopup.suggestTexts.refreshOnPlatforms')}
                                apartment={apartment}
                              />
                            }
                          />
                        </Grid>
                      )}
                      {/* <Typography variant='body1'>
                        <pre className='MuiTypography-root MuiTypography-body1'>
                          {apartment?.information?.damages || ''}
                        </pre>
                      </Typography> */}
                      {/* <Grid
                        item
                        xs={12}
                        className={classes.informationContainer}
                      >
                        <Typography className={classes.infoTitle} variant='h5'>
                          {t('apartment.list.information.renovations')}
                        </Typography>
                        <Typography variant='body1'>
                          <pre className='MuiTypography-root MuiTypography-body1'>
                            {apartment?.information?.renovations || ''}
                          </pre>
                        </Typography>
                      </Grid> */}
                    </Grid>
                  </Hidden>
                </Grid>
              </Grid>

              <ApartmentInformationList
                apartmentInformation={{
                  ...getApartmentBuildingInfo(confirmation, information),
                  ...getAdditionalPropertiesForOverview(information)
                }}
                t={t}
              />
            </Grid>
            {/* <Grid
            item
            xs={12}
            sm={12}
            lg={12}
            className={classes.informationContainer}
          >
            <AdditionalPropertiesList
              apartmentInformation={getAdditionalPropertiesForOverview(
                information
              )}
              t={t}
            />
          </Grid> */}
            <Grid item xs={12} className={classes.informationContainer}>
              <div style={{ marginBottom: '25px', marginTop: '25px' }}>
                <ChipList
                  data={information}
                  // sorting after getting order to make it look nicer
                  informationOrder={getSortedKeyArray(
                    returnTrueFirst,
                    Object.keys(CheckboxPropertyKeys || {}),
                    information
                  )}
                  // for admin show all, for landlord only show true
                  onlyShowTrue={false}
                  translationPath={'apartment.list.information'}
                />
              </div>
              <ApartmentExpansionPanel
                t={t}
                apartment={apartment}
                user={user}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    </Fragment>
  )
}

export default ApartmentInfo
